/**
 * Two kinds of numbers, kept apart on purpose.
 *
 * PIPELINE_* mirror backend/app/constants.py. They are copies for display only — the UI
 * never re-decides anything, it labels what the backend already decided. If a value
 * moves in constants.py it must move here too (the line numbers point at the notebook,
 * as everywhere else in this repo).
 *
 * DISPLAY_* are presentation-only thresholds invented for the state strip. They change
 * how a column is *drawn*, never what the verdict is. Keeping them named and in one
 * place is what stops them from quietly becoming new pipeline heuristics — which the
 * stage rule (CLAUDE.md: "портируем, а не переписываем") forbids.
 */

/** app/constants.py ACTIVE_NDVI_THRESHOLD (notebook 1643). */
export const PIPELINE_ACTIVE_NDVI = 0.3;
/** app/constants.py DEEP_LOW_NDVI_THRESHOLD (notebook 1655). */
export const PIPELINE_DEEP_LOW_NDVI = 0.25;
/** app/constants.py POST_EVENT_LOW_NDVI (notebook 7947). */
export const PIPELINE_POST_EVENT_LOW_NDVI = 0.35;
/** app/constants.py OPTICAL_STALE_DAYS (notebook 7922). */
export const PIPELINE_OPTICAL_STALE_DAYS = 14;
/** app/constants.py MAX_BAD_PIXEL_FRACTION_60D (notebook 3726). */
export const PIPELINE_MAX_BAD_FRACTION = 0.1;
/** app/constants.py MIN_VALID_COVERAGE_FRACTION_60D (notebook 3727). */
export const PIPELINE_MIN_VALID_FRACTION = 0.9;
/**
 * `min_canopy_ndvi` (the latch) is deliberately NOT mirrored here any more. It used to be
 * drawn as a hairline labelled «снова зелёное», which put a gate value and the name of what
 * the gate does on the product surface — a piece of the rule beside a picture of
 * observations. The value lives in the pipeline; nothing on screen needs it.
 */
/** app/constants.py LOOKBACK_DAYS — the 60-day window of cell 2. */
export const PIPELINE_LOOKBACK_DAYS = 60;
/** app/settings.py max_field_area_ha — the cost guard, shown before a run. */
export const PIPELINE_MAX_FIELD_AREA_HA = 500;

/** NDVI band read as "state is not obvious": drawn hatched instead of filled. */
export const DISPLAY_UNCERTAIN_NDVI_LOW = PIPELINE_DEEP_LOW_NDVI;
export const DISPLAY_UNCERTAIN_NDVI_HIGH = PIPELINE_POST_EVENT_LOW_NDVI;
/** Accepted but only just: valid coverage this close to the gate is drawn hatched. */
export const DISPLAY_COMFORT_VALID_FRACTION = 0.95;
/** Snow this visible makes the surface class untrustworthy — hatched, and said aloud. */
export const DISPLAY_SNOW_VISIBLE_FRACTION = 0.1;
/** Presentation-only: above this the snow reason outranks the others. */
export const DISPLAY_SNOW_DOMINANT_FRACTION = 0.4;
/** Top of the NDVI axis of the strip. Above this a bar is simply full height. */
export const DISPLAY_NDVI_AXIS_MAX = 0.9;
/**
 * WORDING boundary, and nothing else: above it the card says «Спад резкий», below
 * it «Спад постепенный». No decision, no colour and no state depends on it — the
 * verdict, its interval and its ruler are identical on either side.
 *
 * 75 is the midpoint of the range the score can actually take on an optical answer: the
 * value saturates at 100 and cannot fall below the middle of the scale, because a step
 * under the trigger produces no answer at all. It is not a detection threshold and must
 * never become one.
 */
export const DISPLAY_SHARP_DROP_SCORE = 75;
/**
 * Field-size policy, mirroring api/schemas.py SMALL_FIELD_AREA_HA / RADAR_UNRELIABLE_AREA_HA
 * (removed research docs (git history) P2-2). These are FALLBACKS only: every field response carries
 * `size` with the server's own verdict and thresholds, and that is what the console reads.
 * The UI used to warn below 1 ha with a pixel-count rationale, so fields of 1-5 ha — where
 * edge contamination puts the mean NDVI above the bare-soil thresholds — got a confident
 * answer and no warning at all.
 */
export const POLICY_SMALL_FIELD_AREA_HA = 5;
export const POLICY_RADAR_UNRELIABLE_AREA_HA = 3;

/**
 * Marker policy, mirroring the server (ST_DWithin over geography, and a count per field).
 *
 * FALLBACKS FOR WORDING ONLY — the server decides both, and its refusals carry a machine
 * `reason` the UI turns into a Russian sentence. The numbers are printed because a limit
 * learned from a 4xx after the click explains nothing at the moment the click is spent.
 *
 * The COUNT has a live source and this constant is only the gap before it arrives:
 * `GET /markers` states the ceiling in its body, and that is what shuts the button and what
 * the 409 sentence quotes. The DISTANCE has no such source — it appears only in the refusal's
 * `detail`, which the client does not read — so this value is the one the hint prints, and it
 * has to move with `repo.MARKER_MAX_DISTANCE_M`.
 */
export const POLICY_MARKER_MAX_DISTANCE_M = 500;
export const POLICY_MARKERS_PER_FIELD = 20;

/**
 * Photo policy, mirroring the server (repo.MAX_PHOTOS_PER_FIELD, settings.max_upload_bytes).
 *
 * FALLBACKS FOR WORDING ONLY, on the same terms as the marker pair above. The COUNT has a
 * live source — `GET /photos` states it — and this value only covers the gap before the first
 * read. The SIZE has none: it appears in no response the client reads, so the refusal sentence
 * quotes this number and it has to move with `settings.max_upload_bytes`.
 *
 * The size is checked here BEFORE the upload, which is not a duplicate of the server's guard
 * but a different one: refusing a 40 MB file locally saves pushing 40 MB up a mobile
 * connection to be told no. The server still checks — it is the one that counts bytes as it
 * reads them rather than trusting a header.
 */
/**
 * db/repo.py MAX_PHOTOS_PER_FIELD. Потолок ПОЛЯ — сколько кадров помещается на весь контур.
 *
 * Стало 30 вместо 10 (notes-v2): десять было потолком поля, пока фотография была сама по
 * себе, а теперь она живёт в заметке, заметок бывает двадцать, и десять кадров на поле
 * означали бы, что половина заметок останется без единого снимка.
 */
export const POLICY_PHOTOS_PER_FIELD = 30;
/**
 * db/repo.py MAX_PHOTOS_PER_NOTE. Второй потолок, и он не про поле, а про ОДНУ заметку.
 *
 * От первого отличается не числом, а тем, где освобождать место: 409 `too_many_photos_note`
 * значит, что на поле места ещё сколько угодно, а вот эта заметка полна. Строка отказа
 * (`photos.limitNote`) говорит ровно это.
 */
export const POLICY_PHOTOS_PER_NOTE = 10;
/** settings.max_upload_bytes, in whole MB — the only one of the two with no live source. */
export const POLICY_PHOTO_MAX_MB = 15;
